import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/Sidebar';
import MobileBottomNav from '../../components/MobileBottomNav';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';

const ANIMATIONS    = [
  { value: 'fadeIn',    label: 'ظهور تدريجي' },
  { value: 'slideDown', label: 'ينزل من الأعلى' },
  { value: 'slideUp',   label: 'يصعد من الأسفل' },
  { value: 'zoom',      label: 'تكبير' },
  { value: 'bounce',    label: 'ارتداد' },
];
const POSITIONS = [
  { value: 'center',        label: 'وسط الشاشة' },
  { value: 'top-center',    label: 'أعلى الوسط' },
  { value: 'bottom-center', label: 'أسفل الوسط' },
  { value: 'top-right',     label: 'أعلى اليمين' },
  { value: 'top-left',      label: 'أعلى اليسار' },
  { value: 'bottom-right',  label: 'أسفل اليمين' },
  { value: 'bottom-left',   label: 'أسفل اليسار' },
];
const BTN_STYLES = [
  { value: 'primary',   label: 'أساسي (أخضر)' },
  { value: 'secondary', label: 'ثانوي (شفاف)' },
  { value: 'danger',    label: 'خطر (أحمر)' },
  { value: 'ghost',     label: 'شفاف' },
];
const BTN_ACTIONS = [
  { value: 'close',    label: 'إغلاق النافذة' },
  { value: 'url',      label: 'فتح رابط (تبويب جديد)' },
  { value: 'navigate', label: 'الانتقال لرابط' },
];
const BTN_ANIMATIONS = [
  { value: 'none',   label: 'بدون' },
  { value: 'pulse',  label: 'نبضة' },
  { value: 'bounce', label: 'ارتداد' },
  { value: 'shake',  label: 'اهتزاز' },
  { value: 'glow',   label: 'توهج' },
];

const DEFAULT_SETTINGS = {
  enabled: false,
  title: '',
  body: '',
  animation: 'fadeIn',
  position: 'center',
  delay_seconds: 3,
  show_once_per_session: true,
  backdrop_blur: true,
  buttons: [],
};

const newBtn = (i) => ({
  id: `btn-${Date.now()}-${i}`,
  text: 'زر جديد',
  url: '',
  action: 'close',
  style: 'primary',
  animation: 'none',
});

const card = {
  background: 'linear-gradient(0deg,rgba(255,255,255,0.02),rgba(255,255,255,0.02)),radial-gradient(92.12% 184.55% at 50% -55%,rgba(40,227,218,0.2) 0%,rgba(40,227,218,0) 100%),#081423',
  border: '1px solid rgba(40,227,218,0.25)',
  borderRadius: '16px',
  padding: '24px',
};

const inputStyle = {
  width: '100%',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: '10px',
  color: '#fff',
  padding: '10px 14px',
  fontSize: '0.9rem',
  outline: 'none',
  direction: 'rtl',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,0.5)' strokeWidth='1.5' fill='none' strokeLinecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'left 12px center',
  paddingLeft: '32px',
};

const labelStyle = {
  display: 'block',
  color: 'rgba(255,255,255,0.75)',
  fontSize: '0.82rem',
  marginBottom: '6px',
  fontWeight: 500,
};

const Toggle = ({ checked, onChange, label }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', userSelect: 'none' }}>
    <div
      onClick={() => onChange(!checked)}
      style={{
        width: '44px', height: '24px', borderRadius: '12px',
        background: checked ? 'rgba(40,227,218,0.7)' : 'rgba(255,255,255,0.15)',
        border: checked ? '1px solid rgba(40,227,218,0.8)' : '1px solid rgba(255,255,255,0.2)',
        position: 'relative', transition: 'all 0.25s ease', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', top: '3px',
        right: checked ? '3px' : '17px',
        width: '16px', height: '16px', borderRadius: '50%',
        background: '#fff', transition: 'right 0.25s ease',
        boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
      }} />
    </div>
    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem' }}>{label}</span>
  </label>
);

export default function PopupSettingsPage() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const authToken = useMemo(() => {
    try { return localStorage.getItem('authToken') || ''; } catch { return ''; }
  }, []);

  useEffect(() => {
    const handle = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handle();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/popup-settings`, {
        headers: { Accept: 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSettings({ ...DEFAULT_SETTINGS, ...data.settings });
      setUpdatedAt(data.updated_at);
    } catch (e) {
      setError('تعذّر تحميل الإعدادات. تأكد من صلاحياتك.');
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/admin/popup-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setSettings({ ...DEFAULT_SETTINGS, ...data.settings });
      setSuccess('تم الحفظ بنجاح!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError('فشل الحفظ. حاول مجدداً.');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const addButton = () => {
    if (settings.buttons.length >= 5) return;
    set('buttons', [...settings.buttons, newBtn(settings.buttons.length)]);
  };

  const removeButton = (id) => set('buttons', settings.buttons.filter(b => b.id !== id));

  const updateButton = (id, key, val) =>
    set('buttons', settings.buttons.map(b => b.id === id ? { ...b, [key]: val } : b));

  const canEdit = role === 'owner' || role === 'developer' || role === 'admin';

  if (!canEdit) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#081423', color: '#fff' }}>
        غير مصرح
      </div>
    );
  }

  const sidebarW = isMobile ? 0 : isTablet ? 80 : 80;

  return (
    <div dir="rtl" style={{ display: 'flex', minHeight: '100vh', background: '#081423', color: '#fff', position: 'relative' }}>
      <Sidebar isOpen={isSidebarOpen} onClose={setIsSidebarOpen} />

      <div style={{ flex: 1, marginRight: isMobile ? 0 : `${sidebarW}px`, padding: isMobile ? '16px 16px 100px' : '32px 32px 40px', transition: 'margin 0.3s' }}>

        {                }
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: isMobile ? '1.25rem' : '1.6rem', fontWeight: 700, marginBottom: '4px' }}>إعدادات النافذة المنبثقة</h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem' }}>
              تحكم بالإعلان الذي يظهر للزوار في الصفحة الرئيسية
              {updatedAt && ` · آخر تحديث: ${new Date(updatedAt).toLocaleString('ar-SA')}`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowPreview(p => !p)}
              style={{ padding: '10px 20px', borderRadius: '10px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600 }}
            >
              {showPreview ? 'إخفاء المعاينة' : 'معاينة'}
            </button>
            <button
              onClick={save}
              disabled={saving || loading}
              style={{
                padding: '10px 24px', borderRadius: '10px', fontWeight: 700, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer',
                background: saving ? 'rgba(40,227,218,0.2)' : 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,0.5) 0%,rgba(40,227,218,0) 100%),rgba(40,227,218,0.25)',
                border: '1px solid rgba(40,227,218,0.4)', color: '#fff',
              }}
            >
              {saving ? 'جارٍ الحفظ...' : 'حفظ الإعدادات'}
            </button>
          </div>
        </div>

        {error   && <div style={{ background: 'rgba(227,40,90,0.15)', border: '1px solid rgba(227,40,90,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#f87171', fontSize: '0.88rem' }}>{error}</div>}
        {success && <div style={{ background: 'rgba(40,227,218,0.12)', border: '1px solid rgba(40,227,218,0.3)', borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', color: '#34d399', fontSize: '0.88rem' }}>{success}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255,255,255,0.5)' }}>جارٍ التحميل...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: showPreview ? (isMobile ? '1fr' : '1fr 380px') : '1fr', gap: '24px', alignItems: 'start' }}>

            {                              }
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {             }
              <div style={card}>
                <h2 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '16px', color: 'rgba(255,255,255,0.9)' }}>الحالة</h2>
                <Toggle checked={settings.enabled} onChange={v => set('enabled', v)} label={settings.enabled ? 'النافذة مفعّلة وستظهر للزوار' : 'النافذة معطّلة'} />
              </div>

              {            }
              <div style={card}>
                <h2 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '16px' }}>النصوص</h2>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>العنوان</label>
                  <input
                    type="text"
                    placeholder="عنوان النافذة المنبثقة"
                    maxLength={200}
                    value={settings.title}
                    onChange={e => set('title', e.target.value)}
                    style={inputStyle}
                  />
                  <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', marginTop: '4px', textAlign: 'left' }}>{settings.title.length}/200</div>
                </div>
                <div>
                  <label style={labelStyle}>النص الرئيسي</label>
                  <textarea
                    placeholder="نص الإعلان أو الرسالة التي تريد إيصالها..."
                    maxLength={1000}
                    rows={4}
                    value={settings.body}
                    onChange={e => set('body', e.target.value)}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
                  />
                  <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', marginTop: '4px', textAlign: 'left' }}>{settings.body.length}/1000</div>
                </div>
              </div>

              {            }
              <div style={card}>
                <h2 style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '16px' }}>إعدادات الظهور</h2>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label style={labelStyle}>انيميشن الظهور</label>
                    <select value={settings.animation} onChange={e => set('animation', e.target.value)} style={selectStyle}>
                      {ANIMATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>موضع النافذة على الشاشة</label>
                    <select value={settings.position} onChange={e => set('position', e.target.value)} style={selectStyle}>
                      {POSITIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={labelStyle}>التأخير قبل الظهور (بالثواني)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <input
                      type="range" min={0} max={60} step={1}
                      value={settings.delay_seconds}
                      onChange={e => set('delay_seconds', Number(e.target.value))}
                      style={{ flex: 1, accentColor: '#28e3da' }}
                    />
                    <span style={{ color: '#28e3da', fontWeight: 700, fontSize: '1rem', minWidth: '42px', textAlign: 'center' }}>
                      {settings.delay_seconds}s
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <Toggle checked={settings.show_once_per_session} onChange={v => set('show_once_per_session', v)} label="عرض مرة واحدة فقط لكل جلسة" />
                  <Toggle checked={settings.backdrop_blur} onChange={v => set('backdrop_blur', v)} label="تعتيم وتضبيب الخلفية" />
                </div>
              </div>

              {             }
              <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <h2 style={{ fontWeight: 700, fontSize: '1rem' }}>الأزرار ({settings.buttons.length}/5)</h2>
                  <button
                    onClick={addButton}
                    disabled={settings.buttons.length >= 5}
                    style={{
                      padding: '7px 16px', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: settings.buttons.length >= 5 ? 'not-allowed' : 'pointer',
                      background: settings.buttons.length >= 5 ? 'rgba(255,255,255,0.05)' : 'rgba(40,227,218,0.15)',
                      border: '1px solid rgba(40,227,218,0.3)', color: settings.buttons.length >= 5 ? 'rgba(255,255,255,0.3)' : '#28e3da',
                    }}
                  >
                    + إضافة زر
                  </button>
                </div>

                {settings.buttons.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px', color: 'rgba(255,255,255,0.35)', fontSize: '0.88rem', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: '10px' }}>
                    لا توجد أزرار بعد. اضغط "إضافة زر" لإضافة واحد.
                  </div>
                )}

                {settings.buttons.map((btn, idx) => (
                  <div key={btn.id} style={{ marginBottom: '16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)' }}>زر {idx + 1}</span>
                      <button
                        onClick={() => removeButton(btn.id)}
                        style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(227,40,90,0.15)', border: '1px solid rgba(227,40,90,0.3)', color: '#f87171', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>نص الزر</label>
                        <input type="text" maxLength={100} value={btn.text} onChange={e => updateButton(btn.id, 'text', e.target.value)} style={inputStyle} placeholder="نص الزر..." />
                      </div>
                      <div>
                        <label style={labelStyle}>الإجراء عند الضغط</label>
                        <select value={btn.action} onChange={e => updateButton(btn.id, 'action', e.target.value)} style={selectStyle}>
                          {BTN_ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                      </div>
                      {(btn.action === 'url' || btn.action === 'navigate') && (
                        <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                          <label style={labelStyle}>الرابط</label>
                          <input type="text" maxLength={500} value={btn.url} onChange={e => updateButton(btn.id, 'url', e.target.value)} style={inputStyle} placeholder="https://..." dir="ltr" />
                        </div>
                      )}
                      <div>
                        <label style={labelStyle}>لون الزر</label>
                        <select value={btn.style} onChange={e => updateButton(btn.id, 'style', e.target.value)} style={selectStyle}>
                          {BTN_STYLES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>انيميشن الزر</label>
                        <select value={btn.animation} onChange={e => updateButton(btn.id, 'animation', e.target.value)} style={selectStyle}>
                          {BTN_ANIMATIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {                        }
            {showPreview && (
              <div style={{ position: isMobile ? 'static' : 'sticky', top: '24px' }}>
                <div style={{ ...card, padding: '16px' }}>
                  <h3 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '12px', color: 'rgba(255,255,255,0.7)' }}>معاينة النافذة</h3>
                  <div style={{ background: 'rgba(8,20,35,0.8)', borderRadius: '12px', padding: '20px', position: 'relative', minHeight: '200px', overflow: 'hidden' }}>
                    {                    }
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,20,35,0.7)', backdropFilter: settings.backdrop_blur ? 'blur(4px)' : 'none' }} />
                    <div style={{
                      position: 'relative', borderRadius: '14px', padding: '20px',
                      background: 'linear-gradient(0deg,rgba(255,255,255,0.02),rgba(255,255,255,0.02)),radial-gradient(92.12% 184.55% at 50% -55%,rgba(40,227,218,0.2) 0%,rgba(40,227,218,0) 100%),#081423',
                      border: '1px solid rgba(40,227,218,0.28)',
                    }} dir="rtl">
                      {              }
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', position: 'absolute', top: '10px', left: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/></svg>
                      </div>
                      {settings.title && <h4 style={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem', marginBottom: '8px', paddingLeft: '28px' }}>{settings.title || 'العنوان'}</h4>}
                      {settings.body  && <p  style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', lineHeight: 1.7, marginBottom: settings.buttons.length ? '14px' : 0 }}>{settings.body}</p>}
                      {settings.buttons.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                          {settings.buttons.map(btn => {
                            const styles = {
                              primary:   { background: 'rgba(40,227,218,0.25)',  border: '1px solid rgba(40,227,218,0.4)',  color: '#fff' },
                              secondary: { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' },
                              danger:    { background: 'rgba(227,40,90,0.25)',   border: '1px solid rgba(227,40,90,0.4)',   color: '#fff' },
                              ghost:     { background: 'transparent',           border: '1px solid rgba(255,255,255,0.15)',color: 'rgba(255,255,255,0.7)' },
                            };
                            return (
                              <span key={btn.id} style={{ padding: '5px 12px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, ...styles[btn.style] }}>
                                {btn.text || 'زر'}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {!settings.title && !settings.body && settings.buttons.length === 0 && (
                        <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', fontSize: '0.82rem', padding: '20px 0' }}>أضف عنوان أو نص لرؤية المعاينة</p>
                      )}
                    </div>
                    <div style={{ position: 'relative', marginTop: '10px', textAlign: 'center' }}>
                      <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)' }}>
                        الموضع: {POSITIONS.find(p => p.value === settings.position)?.label} · الانيميشن: {ANIMATIONS.find(a => a.value === settings.animation)?.label} · تأخير {settings.delay_seconds}s
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isMobile && <MobileBottomNav />}
    </div>
  );
}
